import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "@/lib/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PRIVACY_COPY } from "@/constants/privacy";

const STORAGE_KEY = "elementpad:privacy-notice-ack";

/**
 * One-time privacy summary shown on first visit.
 *
 * - Acknowledgement is persisted in localStorage so subsequent visits stay
 *   silent.
 * - Defers opening to a `useEffect` so SSR/first paint isn't blocked and we
 *   don't read storage during render.
 * - Failing to read/write storage (private mode, etc.) silently no-ops; the
 *   modal will simply re-appear, which is acceptable.
 */
export default function PrivacyNoticeModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const acked = window.localStorage.getItem(STORAGE_KEY);
      if (!acked) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const acknowledge = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Treat any close (Esc, overlay click, X) as acknowledgement so it
        // doesn't keep nagging.
        if (!next) acknowledge();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
            <ShieldCheck size={18} className="text-primary" aria-hidden="true" />
          </div>
          <DialogTitle className="text-center">{PRIVACY_COPY.modalTitle}</DialogTitle>
          <DialogDescription className="text-center">
            {PRIVACY_COPY.shortTagline}
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
          <p>{PRIVACY_COPY.noServerSummary}</p>
          <p>{PRIVACY_COPY.localPrefsSummary}</p>
          <p>ElementPad is an independent, personal project to make my teaching job easier. The goal is to unify scattered chemistry resources and help other teachers and learners in the most friendly way.</p>
        </div>

        <DialogFooter className="sm:justify-between gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to={PRIVACY_COPY.policyPath} onClick={acknowledge}>
              {PRIVACY_COPY.ctaReadFullPolicy}
            </Link>
          </Button>
          <Button onClick={acknowledge} size="sm">
            {PRIVACY_COPY.ctaAcknowledge}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}